import { Injectable, Type, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
// eslint-disable-next-line td-wb-rules/uirouter-import-rule
import { StateDeclaration } from '@uirouter/core';
import { InitialStateService } from '../../services/router/initial-state.service';
import { RouterBaseService } from '../../services/router/router-base.service';

export interface GlobalNotificationBanner {
  id: string;
  component: Type<unknown>;
  inputs?: Record<string, unknown>;
  showOnHomePageOnly?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalNotificationBannerService {
  private readonly initialStateService = inject(InitialStateService);
  private readonly routerService = inject(RouterBaseService);

  readonly onBannersChanged: BehaviorSubject<Array<GlobalNotificationBanner>>;
  readonly onBannerRemoved: Subject<string>;
  readonly currentlyDisplayedBanners: BehaviorSubject<Array<GlobalNotificationBanner>>;

  private readonly banners: Array<GlobalNotificationBanner> = [];

  private isUsersHomePage: boolean;
  private hasReachedFirstTransition: boolean = false;

  constructor() {
    this.onBannersChanged = new BehaviorSubject<Array<GlobalNotificationBanner>>(this.banners);
    this.onBannerRemoved = new Subject<string>();
    this.currentlyDisplayedBanners = new BehaviorSubject<Array<GlobalNotificationBanner>>(this.retrieveCurrentlyDisplayedBanners());
  }

  // This function should be called in an init service (such as webbroker-init-page.service.ts) in order to set up the transition hook
  init(): void {
    this.routerService.setGlobalNotificationBanner((to: StateDeclaration) => {
      this.hasReachedFirstTransition = true;
      const usersInitialState = this.initialStateService.getInitialState();
      const toState = to;
      const wasUsersHomePage = this.isUsersHomePage;
      this.isUsersHomePage = this.matchByUrl(usersInitialState?.url, toState);
      if (wasUsersHomePage !== this.isUsersHomePage && this.banners.length > 0) {
        this.updateCurrentlyDisplayedBanners();
      }
    });
  }

  registerBanner(banner: GlobalNotificationBanner): void {
    if (banner !== undefined && banner !== null && banner.component !== undefined) {
      this.banners.push(banner);
      this.onBannersChanged.next(this.banners);
      this.currentlyDisplayedBanners.next(this.retrieveCurrentlyDisplayedBanners());
    }
  }

  removeBanner(id: string): void {
    const index = this.banners.findIndex((banner: GlobalNotificationBanner): boolean => banner.id === id);
    if (index >= 0) {
      this.banners.splice(index, 1);
      this.onBannersChanged.next(this.banners);
      this.currentlyDisplayedBanners.next(this.retrieveCurrentlyDisplayedBanners());
      this.onBannerRemoved.next(id);
    }
  }

  retrieveCurrentlyDisplayedBanners(): Array<GlobalNotificationBanner> {
    // Do not display anything on screen until the first page transition has triggered
    // Otherwise you'll see non-homepage banners only and homepage banners will "pop in" after the transition is done
    if (!this.hasReachedFirstTransition) {
      return [];
    }
    return this.isUsersHomePage ? this.banners :
      this.banners.filter((banner: GlobalNotificationBanner): boolean => !!banner.showOnHomePageOnly === false);
  }

  isBannerDisplayed(bannerId: string): boolean {
    return this.retrieveCurrentlyDisplayedBanners().some(
      (banner: GlobalNotificationBanner): boolean => banner.id === bannerId
    );
  }

  private updateCurrentlyDisplayedBanners(): void {
    this.currentlyDisplayedBanners.next(this.retrieveCurrentlyDisplayedBanners());
  }

  private matchByUrl(homepageUrl: string, toState: StateDeclaration): boolean {
    const noHashHomepageUrl = homepageUrl?.indexOf('#') !== -1 ? homepageUrl.substring(homepageUrl.indexOf('#') + 1) : homepageUrl;
    // returns full url (such as #/page/home/summary or #/page/rrif/payment/edit?accountId=sqjA0Ii1-Y3uXPNNx7TklLK_HwhxSjFsxWvIRM1aWpo)
    const toStateUrl = this.routerService.getStateHref(toState?.name);
    const noHashToStateUrl = toStateUrl?.indexOf('#') !== -1 ? toStateUrl.substring(toStateUrl.indexOf('#') + 1) : toStateUrl;
    // Check using indexOf to disregard url parameters
    return noHashToStateUrl.indexOf(noHashHomepageUrl) !== -1;
  }
}
